import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import logHeatmapFilterMenuClosed from '@alltrails/amplitude/events/logHeatmapFilterMenuClosed';
import logHeatmapFilterTapped from '@alltrails/amplitude/events/logHeatmapFilterTapped';
import Typography from '@alltrails/core/components/Typography';
import { Modal } from '@alltrails/modal';
import { useGetHeatmapOptionsDataQuery } from '../../redux/heatmapsApi';
import { updateCurrentlySelectedOptions, updateIsHeatmapFilterModalOpen } from '../../redux/heatmapsSlice';
import { useAppDispatch, useAppSelector } from '../../redux/helpers';
import { HeatmapActivityOption } from '../../types/HeatmapActivity';
import { HeatmapOptionKey } from '../../types/HeatmapOptionsData';
import { HeatmapTimeRangeOption } from '../../types/HeatmapTimeRange';
import styles from './HeatmapFilterModal.module.scss';
import OptionChips from './OptionChips';

const HeatmapFilterModal = () => {
  const dispatch = useAppDispatch();
  const { availableOptions, currentlySelectedOptions, isHeatmapFilterModalOpen } = useAppSelector(state => ({
    availableOptions: state.heatmaps.availableOptions,
    currentlySelectedOptions: state.heatmaps.currentlySelectedOptions,
    isHeatmapFilterModalOpen: state.heatmaps.isHeatmapFilterModalOpen
  }));
  const { isError } = useGetHeatmapOptionsDataQuery();

  const handleUpdateCurrentlySelectedOption = useCallback(
    (optionKey: HeatmapOptionKey, option: HeatmapActivityOption | HeatmapTimeRangeOption) => {
      const activityTapped = optionKey === 'activity' ? option?.value : undefined;
      const timeRangeTapped = optionKey === 'time_range' ? option?.value : undefined;
      logHeatmapFilterTapped({
        activity_tapped: activityTapped,
        time_range_tapped: timeRangeTapped
      });
      dispatch(updateCurrentlySelectedOptions({ [optionKey]: option } as Record<HeatmapOptionKey, HeatmapActivityOption | HeatmapTimeRangeOption>));
    },
    [dispatch]
  );

  const handleCloseModal = useCallback(() => {
    logHeatmapFilterMenuClosed();
    dispatch(updateIsHeatmapFilterModalOpen(false));
  }, [dispatch]);

  if (!availableOptions || !isHeatmapFilterModalOpen) return null;

  return (
    <Modal
      isOpen={isHeatmapFilterModalOpen}
      onCloseRequest={handleCloseModal}
      title={!isError ? <FormattedMessage defaultMessage="Heatmap options" /> : null}
      ariaLabelElementId="heatmap-filter-modal"
    >
      <div className={styles.heatmapFilterModal}>
        <h3 className={styles.visuallyHidden} id="heatmap-filter-modal">
          <FormattedMessage defaultMessage="Heatmap options" />
        </h3>
        {!!availableOptions &&
          availableOptions?.map(option => (
            <div className={styles.cardContainer} key={option?.key}>
              <Typography variant="text200bold">{option?.name}</Typography>
              <div className={styles.cards}>
                <OptionChips
                  title={option?.name}
                  chips={option?.values}
                  selectedChip={currentlySelectedOptions?.[option?.key]}
                  onChange={selectedChip => handleUpdateCurrentlySelectedOption(option?.key, selectedChip)}
                />
              </div>
            </div>
          ))}
      </div>
    </Modal>
  );
};

export default HeatmapFilterModal;
